import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const OAUTH_REDIRECT_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Global hook that checks if the user needs to be redirected back to their
 * original page after an OAuth flow (e.g. Canva integration).
 *
 * After OAuth, the backend may redirect to any frontend page (e.g. /integrations,
 * /social-media-hub, /dashboard). This hook detects that scenario by checking
 * sessionStorage and redirects the user back to where they started.
 */
export const useOAuthRedirectHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const redirectPath = sessionStorage.getItem('oauth_redirect_after_auth');
    const redirectTimestamp = sessionStorage.getItem('oauth_redirect_timestamp');

    if (!redirectPath || !redirectTimestamp) return;

    // Only redirect if the stored path is recent (within 5 minutes)
    const elapsed = Date.now() - parseInt(redirectTimestamp, 10);
    if (elapsed > OAUTH_REDIRECT_MAX_AGE_MS) {
      // Stale redirect data - clean up
      sessionStorage.removeItem('oauth_redirect_after_auth');
      sessionStorage.removeItem('oauth_redirect_timestamp');
      return;
    }

    // Don't redirect if we're already on the target path
    const currentFullPath = location.pathname + location.search;
    if (currentFullPath === redirectPath || location.pathname === redirectPath) {
      // We've arrived at the destination - clean up timestamp only
      // (keep oauth_canva_config for MediaPostCard to detect)
      sessionStorage.removeItem('oauth_redirect_after_auth');
      sessionStorage.removeItem('oauth_redirect_timestamp');
      return;
    }

    // We're on a different page (the OAuth landing page) - redirect to stored path
    sessionStorage.removeItem('oauth_redirect_after_auth');
    sessionStorage.removeItem('oauth_redirect_timestamp');
    navigate(redirectPath, { replace: true });
  }, [location.pathname, location.search, navigate]);
};
